import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/contexts/LanguageContext";

// Lazy-loaded so dashboard hooks/queries are NOT bundled into public marketing/academy routes
const MSPDashboardProviderLazy = lazy(() => import("@/hooks/MSPDashboardProviderLazy"));
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageLoader } from "@/components/PageLoader";
import { CookieBanner } from "@/components/cookie/CookieBanner";
import { GoogleAnalyticsProvider } from "@/components/analytics";

// Eagerly loaded pages (critical path — landing page only)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// High-traffic but not landing page — lazy loaded
const Vergelijken = lazy(() => import('./pages/Vergelijken'));
const MSPDetail = lazy(() => import('./pages/MSPDetail'));

// Auth pages (lazy loaded)
const Login = lazy(() => import('./pages/auth/Login'));
const RegisterChoice = lazy(() => import('./pages/auth/RegisterChoice'));
const AuthRedirect = lazy(() => import('./pages/auth/AuthRedirect'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const ClaimConfirm = lazy(() => import('./pages/ClaimConfirm'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const UpdatePassword = lazy(() => import('./pages/auth/UpdatePassword'));
const OAuthConsent = lazy(() => import('./pages/auth/OAuthConsent'));
const MijnAccount = lazy(() => import('./pages/MijnAccount'));

// Lazy loaded pages (code splitting for performance)
const VergelijkMatrix = lazy(() => import('./pages/VergelijkMatrix'));
const Wizard = lazy(() => import('./pages/Wizard'));
const AiMatch = lazy(() => import('./pages/AiMatch'));
const Academy = lazy(() => import('./pages/Academy'));
const ArtikelDetail = lazy(() => import('./pages/ArtikelDetail'));
const VoorMSPs = lazy(() => import('./pages/VoorMSPs'));
const MspKlantenVinden = lazy(() => import('./pages/voor-msps/MspKlantenVinden'));
const ZoWerktHet = lazy(() => import('./pages/ZoWerktHet'));
const OverOns = lazy(() => import('./pages/OverOns'));
const Contact = lazy(() => import('./pages/Contact'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Voorwaarden = lazy(() => import('./pages/Voorwaarden'));
const Cookies = lazy(() => import('./pages/Cookies'));
const FAQ = lazy(() => import('./pages/FAQ'));

const ItBedrijfVergelijken = lazy(() => import('./pages/ItBedrijfVergelijken'));
const ManagedServiceProviderVergelijken = lazy(() => import('./pages/ManagedServiceProviderVergelijken'));
const ItDienstverlenerVergelijken = lazy(() => import('./pages/ItDienstverlenerVergelijken'));
const NIS2ItPartner = lazy(() => import('./pages/NIS2ItPartner'));
const NIS2ChecklistMKB = lazy(() => import('./pages/NIS2ChecklistMKB'));
const NIS2SupplyChain = lazy(() => import('./pages/NIS2SupplyChain'));
const NIS2Aantoonbaarheid = lazy(() => import('./pages/NIS2Aantoonbaarheid'));
const NIS2VragenAanMSP = lazy(() => import('./pages/NIS2VragenAanMSP'));
const WerkplekbeheerUitbesteden = lazy(() => import('./pages/WerkplekbeheerUitbesteden'));
const ItBeheerUitbesteden = lazy(() => import('./pages/ItBeheerUitbesteden'));
const ItBeheerUitbestedenMKB = lazy(() => import('./pages/ItBeheerUitbestedenMKB'));
const IctBeheerUitbesteden = lazy(() => import('./pages/IctBeheerUitbesteden'));
const CybersecurityMKB = lazy(() => import('./pages/CybersecurityMKB'));
const WerkplekbeheerMKB = lazy(() => import('./pages/WerkplekbeheerMKB'));
const Microsoft365BeheerUitbesteden = lazy(() => import('./pages/Microsoft365BeheerUitbesteden'));
const ServerbeheerUitbesteden = lazy(() => import('./pages/ServerbeheerUitbesteden'));
const SysteembeheerUitbesteden = lazy(() => import('./pages/SysteembeheerUitbesteden'));
const BackupEnHerstelMKB = lazy(() => import('./pages/BackupEnHerstelMKB'));
const WatKostEenMSP = lazy(() => import('./pages/WatKostEenMSP'));
const BesteMSPBedrijvenNederland = lazy(() => import('./pages/BesteMSPBedrijvenNederland'));
const MSPvsITBedrijf = lazy(() => import('./pages/MSPvsITBedrijf'));
const NetwerkbeheerUitbesteden = lazy(() => import('./pages/NetwerkbeheerUitbesteden'));
const ItDienstverlenerZoeken = lazy(() => import('./pages/ItDienstverlenerZoeken'));
const Methodologie = lazy(() => import('./pages/Methodologie'));
const EditorialPolicy = lazy(() => import('./pages/EditorialPolicy'));

// SEA campaign landing pages (Google Ads — noindex, /campagne/* URL-prefix)
const ItHelpdeskUitbestedenMKB = lazy(() => import('./pages/ItHelpdeskUitbestedenMKB'));
const CloudBeheerUitbesteden = lazy(() => import('./pages/CloudBeheerUitbesteden'));
const SeaServerBackup = lazy(() => import('./pages/sea/ServerEnBackupBeheer'));
const SeaItBeveiliging = lazy(() => import('./pages/sea/ItBeveiligingMkb'));
const SeaIctBeheer = lazy(() => import('./pages/sea/IctBeheerUitbesteden'));
const SeaIctBeheerKostenOfferte = lazy(() => import('./pages/sea/IctBeheerKostenOfferte'));
const SeaItHelpdesk = lazy(() => import('./pages/sea/ItHelpdeskUitbesteden'));
const SeaMicrosoft365 = lazy(() => import('./pages/sea/Microsoft365BeheerUitbesteden'));
const SeaWerkplekbeheer = lazy(() => import('./pages/sea/WerkplekbeheerUitbestedenSea'));
const SeaNis2ItBeveiliging = lazy(() => import('./pages/sea/Nis2ItBeveiliging'));
const SeaAiAutomatisering = lazy(() => import('./pages/sea/AiAutomatiseringMkb'));

// Guide pages
const MspSelectiegesprekChecklist = lazy(() => import('./pages/gids/MspSelectiegesprekChecklist'));

// City landing pages (lazy loaded)
const MspAmsterdam = lazy(() => import('./pages/city/MspAmsterdam'));
const MspRotterdam = lazy(() => import('./pages/city/MspRotterdam'));
const MspDenHaag = lazy(() => import('./pages/city/MspDenHaag'));
const MspUtrecht = lazy(() => import('./pages/city/MspUtrecht'));
const MspEindhoven = lazy(() => import('./pages/city/MspEindhoven'));
const MspTilburg = lazy(() => import('./pages/city/MspTilburg'));
const MspGroningen = lazy(() => import('./pages/city/MspGroningen'));
const MspAlmere = lazy(() => import('./pages/city/MspAlmere'));
const MspBreda = lazy(() => import('./pages/city/MspBreda'));
const MspNijmegen = lazy(() => import('./pages/city/MspNijmegen'));
const MspHaarlem = lazy(() => import('./pages/city/MspHaarlem'));
const MspArnhem = lazy(() => import('./pages/city/MspArnhem'));
const MspEnschede = lazy(() => import('./pages/city/MspEnschede'));
const MspAmersfoort = lazy(() => import('./pages/city/MspAmersfoort'));
const MspApeldoorn = lazy(() => import('./pages/city/MspApeldoorn'));
const MspDenBosch = lazy(() => import('./pages/city/MspDenBosch'));
const MspZwolle = lazy(() => import('./pages/city/MspZwolle'));
const MspZoetermeer = lazy(() => import('./pages/city/MspZoetermeer'));
const MspLeiden = lazy(() => import('./pages/city/MspLeiden'));
const MspMaastricht = lazy(() => import('./pages/city/MspMaastricht'));
const MspDordrecht = lazy(() => import('./pages/city/MspDordrecht'));
const MspDeventer = lazy(() => import('./pages/city/MspDeventer'));
const MspDelft = lazy(() => import('./pages/city/MspDelft'));
const MspZaandam = lazy(() => import('./pages/city/MspZaandam'));
const MspLeeuwarden = lazy(() => import('./pages/city/MspLeeuwarden'));

// Admin pages (lazy loaded)
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const PageBuilderPage = lazy(() => import('./pages/admin/PageBuilderPage'));
const MSPForm = lazy(() => import('./pages/admin/MSPForm'));
// Legacy article form removed — content managed via Content Kalender
const AdminMSPDashboard = lazy(() => import('./pages/admin/AdminMSPDashboard'));
const AdminAccountsDashboard = lazy(() => import('./pages/admin/AdminAccountsDashboard'));
const AdminLeadsDashboard = lazy(() => import('./pages/admin/AdminLeadsDashboard'));
const AdminLeadPerformance = lazy(() => import('./pages/admin/AdminLeadPerformance'));
const AdminMarketingDashboard = lazy(() => import('./pages/admin/AdminMarketingDashboard'));
const AdminMarketingCampaigns = lazy(() => import('./pages/admin/AdminMarketingCampaigns'));
const AdminSeoDashboard = lazy(() => import('./pages/admin/AdminSeoDashboard'));
const AdminEmailCampagnes = lazy(() => import('./pages/admin/AdminEmailCampagnes'));
const AdminFinancialDashboard = lazy(() => import('./pages/admin/AdminFinancialDashboard'));
const AdminBillingApprovals = lazy(() => import('./pages/admin/AdminBillingApprovals'));
const AdminLeadDisputes = lazy(() => import('./pages/admin/AdminLeadDisputes'));
const AdminNis2Matching = lazy(() => import('./pages/admin/AdminNis2Matching'));

const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminAuditLogs = lazy(() => import('./pages/admin/AdminAuditLogs'));
const AdminEmailLogs = lazy(() => import('./pages/admin/AdminEmailLogs'));
const AdminEmailTemplates = lazy(() => import('./pages/admin/AdminEmailTemplates'));
const AdminCertifications = lazy(() => import('./pages/admin/AdminCertifications'));
const AdminClaims = lazy(() => import('./pages/admin/AdminClaims'));
const AdminContentCalendar = lazy(() => import('./pages/admin/AdminContentCalendar'));
const AdminContentClusters = lazy(() => import('./pages/admin/AdminContentClusters'));
const AdminLandingPagesOverview = lazy(() => import('./pages/admin/AdminLandingPagesOverview'));
const AdminHubspotSync = lazy(() => import('./pages/admin/AdminHubspotSync'));

// Dashboard pages (lazy loaded)
const DashboardHome = lazy(() => import('./pages/dashboard/DashboardHome'));
const DashboardProfiel = lazy(() => import('./pages/dashboard/DashboardProfiel'));
const DashboardLeads = lazy(() => import('./pages/dashboard/DashboardLeads'));
const DashboardReviews = lazy(() => import('./pages/dashboard/DashboardReviews'));
const DashboardStatistieken = lazy(() => import('./pages/dashboard/DashboardStatistieken'));
const DashboardAbonnement = lazy(() => import('./pages/dashboard/DashboardAbonnement'));
const DashboardAccount = lazy(() => import('./pages/dashboard/DashboardAccount'));
const DashboardMijnMSP = lazy(() => import('./pages/dashboard/DashboardMijnMSP'));
const DashboardTeam = lazy(() => import('./pages/dashboard/DashboardTeam'));
const DashboardBillingHistory = lazy(() => import('./pages/dashboard/DashboardBillingHistory'));
const DashboardLeadBilling = lazy(() => import('./pages/dashboard/DashboardLeadBilling'));
const AcceptInvite = lazy(() => import('./pages/dashboard/AcceptInvite'));
const DashboardCertifications = lazy(() => import('./pages/dashboard/DashboardCertifications'));
const DashboardLeadMatching = lazy(() => import('./pages/dashboard/DashboardLeadMatching'));
const DashboardPending = lazy(() => import('./pages/dashboard/DashboardPending'));
const Nis2Zelfscan = lazy(() => import('./pages/Nis2Zelfscan'));

const queryClient = new QueryClient();

/** Wraps dashboard routes with MSPDashboardProvider — lazy-loaded so dashboard code is excluded from public bundles */
function DashboardWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <MSPDashboardProviderLazy>{children}</MSPDashboardProviderLazy>
    </Suspense>
  );
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" forcedTheme="light" enableSystem={false}>
        <LanguageProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <GoogleAnalyticsProvider>
                  <ScrollToTop />
                  <CookieBanner />
                  <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/vergelijken" element={<Vergelijken />} />
                      <Route path="/vergelijken/matrix" element={<VergelijkMatrix />} />
                      <Route path="/msp/:slug" element={<MSPDetail />} />
                      <Route path="/wizard" element={<Wizard />} />
                      <Route path="/ai-match" element={<AiMatch />} />
                      <Route path="/academy" element={<Academy />} />
                      <Route path="/academy/:slug" element={<ArtikelDetail />} />
                      <Route path="/voor-msps" element={<VoorMSPs />} />
                      <Route path="/voor-msps/msp-klanten-vinden" element={<MspKlantenVinden />} />
                      <Route path="/zo-werkt-het" element={<ZoWerktHet />} />
                      <Route path="/over-ons" element={<OverOns />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/voorwaarden" element={<Voorwaarden />} />
                      <Route path="/cookies" element={<Cookies />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/beta" element={<Navigate to="/" replace />} />
                      <Route path="/it-bedrijf-vergelijken" element={<ItBedrijfVergelijken />} />
                      <Route path="/managed-service-provider-vergelijken" element={<ManagedServiceProviderVergelijken />} />
                      <Route path="/it-dienstverlener-vergelijken" element={<ItDienstverlenerVergelijken />} />
                      <Route path="/nis2-it-partner" element={<NIS2ItPartner />} />
                      <Route path="/nis2-checklist-mkb" element={<NIS2ChecklistMKB />} />
                      <Route path="/nis2-supply-chain" element={<NIS2SupplyChain />} />
                      <Route path="/nis2-aantoonbaarheid" element={<NIS2Aantoonbaarheid />} />
                      <Route path="/nis2-vragen-aan-msp" element={<NIS2VragenAanMSP />} />
                      <Route path="/werkplekbeheer-uitbesteden" element={<WerkplekbeheerUitbesteden />} />
                      <Route path="/it-beheer-uitbesteden" element={<ItBeheerUitbesteden />} />
                      <Route path="/it-beheer-uitbesteden-mkb" element={<ItBeheerUitbestedenMKB />} />
                      <Route path="/ict-beheer-uitbesteden" element={<IctBeheerUitbesteden />} />
                      <Route path="/cybersecurity-mkb" element={<CybersecurityMKB />} />
                      <Route path="/werkplekbeheer-mkb" element={<WerkplekbeheerMKB />} />
                      <Route path="/wat-kost-een-msp" element={<WatKostEenMSP />} />
                      <Route path="/beste-msp-bedrijven-nederland" element={<BesteMSPBedrijvenNederland />} />
                      <Route path="/msp-vs-it-bedrijf" element={<MSPvsITBedrijf />} />
                      <Route path="/netwerkbeheer-uitbesteden" element={<NetwerkbeheerUitbesteden />} />
                      <Route path="/it-dienstverlener-zoeken" element={<ItDienstverlenerZoeken />} />
                      <Route path="/methodologie" element={<Methodologie />} />
                      <Route path="/editorial-policy" element={<EditorialPolicy />} />
                      <Route path="/nis2-zelfscan" element={<Nis2Zelfscan />} />
                      {/* SEA campaign landing pages (Google Ads — noindex, not in sitemap) */}
                      <Route path="/campagne/ict-beheer" element={<SeaIctBeheer />} />
                      <Route path="/campagne/ict-beheer-kosten-offerte" element={<SeaIctBeheerKostenOfferte />} />
                      <Route path="/campagne/it-helpdesk" element={<SeaItHelpdesk />} />
                      <Route path="/campagne/werkplekbeheer" element={<SeaWerkplekbeheer />} />
                      <Route path="/campagne/microsoft-365" element={<SeaMicrosoft365 />} />
                      <Route path="/campagne/it-beveiliging" element={<SeaItBeveiliging />} />
                      <Route path="/campagne/server-backup" element={<SeaServerBackup />} />
                      <Route path="/campagne/nis2-it-beveiliging" element={<SeaNis2ItBeveiliging />} />
                      <Route path="/campagne/ai-automatisering-mkb" element={<SeaAiAutomatisering />} />
                      {/* SEO routes that previously also acted as Google Ads targets — kept SEO */}
                      <Route path="/it-helpdesk-uitbesteden" element={<ItHelpdeskUitbestedenMKB />} />
                      <Route path="/cloud-beheer-uitbesteden" element={<CloudBeheerUitbesteden />} />
                      <Route path="/microsoft-365-beheer-uitbesteden" element={<Microsoft365BeheerUitbesteden />} />
                      <Route path="/serverbeheer-uitbesteden" element={<ServerbeheerUitbesteden />} />
                      <Route path="/systeembeheer-uitbesteden" element={<SysteembeheerUitbesteden />} />
                      <Route path="/backup-en-herstel-mkb" element={<BackupEnHerstelMKB />} />
                      {/* Legacy SEA URLs — kept reachable so live Google Ads creatives don't 404. Canonical points to /campagne/* */}
                      <Route path="/server-en-backup-beheer" element={<SeaServerBackup />} />
                      <Route path="/it-beveiliging-mkb" element={<SeaItBeveiliging />} />
                      {/* Guide pages */}
                      <Route path="/gids/msp-selectiegesprek-checklist" element={<MspSelectiegesprekChecklist />} />
                      {/* City landing pages */}
                      <Route path="/msp-amsterdam" element={<MspAmsterdam />} />
                      <Route path="/msp-rotterdam" element={<MspRotterdam />} />
                      <Route path="/msp-den-haag" element={<MspDenHaag />} />
                      <Route path="/msp-utrecht" element={<MspUtrecht />} />
                      <Route path="/msp-eindhoven" element={<MspEindhoven />} />
                      <Route path="/msp-tilburg" element={<MspTilburg />} />
                      <Route path="/msp-groningen" element={<MspGroningen />} />
                      <Route path="/msp-almere" element={<MspAlmere />} />
                      <Route path="/msp-breda" element={<MspBreda />} />
                      <Route path="/msp-nijmegen" element={<MspNijmegen />} />
                      <Route path="/msp-haarlem" element={<MspHaarlem />} />
                      <Route path="/msp-arnhem" element={<MspArnhem />} />
                      <Route path="/msp-enschede" element={<MspEnschede />} />
                      <Route path="/msp-amersfoort" element={<MspAmersfoort />} />
                      <Route path="/msp-apeldoorn" element={<MspApeldoorn />} />
                      <Route path="/msp-den-bosch" element={<MspDenBosch />} />
                      <Route path="/msp-zwolle" element={<MspZwolle />} />
                      <Route path="/msp-zoetermeer" element={<MspZoetermeer />} />
                      <Route path="/msp-leiden" element={<MspLeiden />} />
                      <Route path="/msp-maastricht" element={<MspMaastricht />} />
                      <Route path="/msp-dordrecht" element={<MspDordrecht />} />
                      <Route path="/msp-deventer" element={<MspDeventer />} />
                      <Route path="/msp-delft" element={<MspDelft />} />
                      <Route path="/msp-zaandam" element={<MspZaandam />} />
                      <Route path="/msp-leeuwarden" element={<MspLeeuwarden />} />
                      {/* Auth routes */}
                      <Route path="/auth/login" element={<Login />} />
                      <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                      <Route path="/auth/register" element={<RegisterChoice />} />
                      <Route path="/auth/verify-email" element={<VerifyEmail />} />
                      <Route path="/claim/bevestigen" element={<ClaimConfirm />} />
                      <Route path="/auth" element={<AuthRedirect />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/update-password" element={<UpdatePassword />} />
                      {/* MKB profile */}
                      <Route path="/mijn-account" element={<MijnAccount />} />
                      {/* Admin routes */}
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/admin/settings" element={<AdminSettings />} />
                      <Route path="/admin/msps" element={<AdminMSPDashboard />} />
                      <Route path="/admin/accounts" element={<AdminAccountsDashboard />} />
                      <Route path="/admin/marketing/leads" element={<AdminLeadsDashboard />} />
                      <Route path="/admin/lead-performance" element={<AdminLeadPerformance />} />
                      <Route path="/admin/leads" element={<Navigate to="/admin/marketing/leads" replace />} />
                      <Route path="/admin/mkb" element={<Navigate to="/admin/accounts" replace />} />
                      <Route path="/admin/marketing" element={<AdminMarketingDashboard />} />
                      <Route path="/admin/marketing/campagnes" element={<AdminMarketingCampaigns />} />
                      <Route path="/admin/marketing/seo" element={<AdminSeoDashboard />} />
                      <Route path="/admin/marketing/email-campagnes" element={<AdminEmailCampagnes />} />
                      <Route path="/admin/financial" element={<AdminFinancialDashboard />} />
                      <Route path="/admin/approvals/billing" element={<AdminBillingApprovals />} />
                      <Route path="/admin/approvals/disputes" element={<AdminLeadDisputes />} />
                      <Route path="/admin/nis2/matching" element={<AdminNis2Matching />} />

                      <Route path="/admin/reviews" element={<AdminReviews />} />
                      <Route path="/admin/email-logs" element={<Navigate to="/admin/settings" replace />} />
                      <Route path="/admin/email-templates" element={<Navigate to="/admin/settings" replace />} />
                      <Route path="/admin/audit" element={<Navigate to="/admin/settings" replace />} />
                      <Route path="/admin/certifications" element={<AdminCertifications />} />
                      <Route path="/admin/claims" element={<AdminClaims />} />
                      <Route path="/admin/content-kalender" element={<AdminContentCalendar />} />
                      <Route path="/admin/content/clusters" element={<AdminContentClusters />} />
                      <Route path="/admin/content/landing-pages" element={<AdminLandingPagesOverview />} />
                      <Route path="/admin/hubspot-sync" element={<AdminHubspotSync />} />
                      <Route path="/admin/page-builder/:slug" element={<PageBuilderPage />} />
                      <Route path="/admin/msp/:id" element={<MSPForm />} />
                      <Route path="/admin/artikel/:id" element={<Navigate to="/admin/content-kalender" replace />} />
                      {/* MSP Dashboard Routes — wrapped with MSPDashboardProvider */}
                      <Route path="/dashboard" element={<DashboardWrapper><DashboardHome /></DashboardWrapper>} />
                      <Route path="/dashboard/profiel" element={<DashboardWrapper><DashboardProfiel /></DashboardWrapper>} />
                      <Route path="/dashboard/leads" element={<DashboardWrapper><DashboardLeads /></DashboardWrapper>} />
                      <Route path="/dashboard/reviews" element={<DashboardWrapper><DashboardReviews /></DashboardWrapper>} />
                      <Route path="/dashboard/statistieken" element={<DashboardWrapper><DashboardStatistieken /></DashboardWrapper>} />
                      <Route path="/dashboard/abonnement" element={<DashboardWrapper><DashboardAbonnement /></DashboardWrapper>} />
                      <Route path="/dashboard/account" element={<DashboardWrapper><DashboardAccount /></DashboardWrapper>} />
                      <Route path="/dashboard/account/mijn-msp" element={<DashboardWrapper><DashboardMijnMSP /></DashboardWrapper>} />
                      <Route path="/dashboard/team" element={<DashboardWrapper><DashboardTeam /></DashboardWrapper>} />
                      <Route path="/dashboard/lead-facturatie" element={<DashboardWrapper><DashboardLeadBilling /></DashboardWrapper>} />
                      <Route path="/dashboard/facturen" element={<DashboardWrapper><DashboardBillingHistory /></DashboardWrapper>} />
                      <Route path="/dashboard/billing" element={<Navigate to="/dashboard/facturen" replace />} />
                      <Route path="/dashboard/lead-matching" element={<DashboardWrapper><DashboardLeadMatching /></DashboardWrapper>} />
                      <Route path="/dashboard/certifications" element={<DashboardWrapper><DashboardCertifications /></DashboardWrapper>} />
                      <Route path="/dashboard/compliance" element={<DashboardWrapper><DashboardCertifications /></DashboardWrapper>} />
                      {/* Accept Invite — no DashboardWrapper (Fix 1: auth-flow only) */}
                      <Route path="/dashboard/accept-invite" element={<AcceptInvite />} />
                      <Route path="/dashboard/pending" element={<DashboardPending />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  </ErrorBoundary>
                </GoogleAnalyticsProvider>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
